import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Progress } from './ui/progress';
import { 
  FolderCode, 
  Plus, 
  Trophy, 
  Target,
  CheckCircle2,
  Circle,
  Lock,
  Sparkles,
  ChevronLeft,
} from 'lucide-react';

// Mock projects data
const mockProjects = [
  { id: 1, name: 'שלום עולם', lastEdited: 'היום', completed: true },
  { id: 2, name: 'לולאות מגניבות', lastEdited: 'אתמול', completed: true },
  { id: 3, name: 'מחשבון פשוט', lastEdited: 'לפני 3 ימים', completed: false },
];

// Mock learning path
const learningPath = [
  { id: 1, title: 'מבוא לפייתון', description: 'הכר את השפה', completed: true, xp: 50 },
  { id: 2, title: 'משתנים וטיפוסים', description: 'מספרים, טקסט ועוד', completed: true, xp: 75 },
  { id: 3, title: 'תנאים (if/else)', description: 'קבלת החלטות בקוד', completed: false, xp: 100, current: true },
  { id: 4, title: 'לולאות', description: 'חזרה על פעולות', completed: false, xp: 100, locked: false },
  { id: 5, title: 'פונקציות', description: 'קוד מאורגן ויעיל', completed: false, xp: 150, locked: true },
  { id: 6, title: 'רשימות', description: 'אוספים של נתונים', completed: false, xp: 150, locked: true },
];

const ProjectItem = ({ project }) => (
  <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group">
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
      project.completed ? 'bg-success/20 text-success' : 'bg-primary/20 text-primary'
    }`}>
      {project.completed ? <CheckCircle2 className="h-4 w-4" /> : <FolderCode className="h-4 w-4" />}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium truncate">{project.name}</p>
      <p className="text-xs text-muted-foreground">{project.lastEdited}</p>
    </div>
    <ChevronLeft className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity rtl-flip" />
  </div>
);

const PathItem = ({ item, index }) => (
  <div className={`relative flex gap-3 pb-6 ${item.locked ? 'opacity-50' : ''}`}>
    {/* Connector line */}
    {index < learningPath.length - 1 && (
      <div className={`absolute top-8 right-4 w-0.5 h-full ${
        item.completed ? 'bg-success' : 'bg-border'
      }`} />
    )}
    
    {/* Status icon */}
    <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
      item.completed 
        ? 'bg-success text-success-foreground' 
        : item.current 
          ? 'bg-primary text-primary-foreground animate-pulse-glow' 
          : item.locked
            ? 'bg-muted text-muted-foreground'
            : 'bg-muted text-foreground'
    }`}>
      {item.completed ? (
        <CheckCircle2 className="h-4 w-4" />
      ) : item.locked ? (
        <Lock className="h-3 w-3" />
      ) : item.current ? (
        <Target className="h-4 w-4" />
      ) : (
        <Circle className="h-4 w-4" />
      )}
    </div>
    
    {/* Content */}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className={`text-sm font-medium ${item.current ? 'text-primary' : ''}`}>
          {item.title}
        </p>
        {item.current && (
          <Badge className="bg-primary/20 text-primary text-[10px] h-4 px-1.5">
            עכשיו
          </Badge>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
      <div className="flex items-center gap-1 mt-1">
        <Sparkles className="h-3 w-3 text-gamification-xp" />
        <span className="text-xs text-gamification-xp font-medium">{item.xp} XP</span>
      </div>
    </div>
  </div>
);

export const Sidebar = ({ gamificationEnabled = true }) => {
  const completedLessons = learningPath.filter(l => l.completed).length;
  const totalLessons = learningPath.length;
  const progress = (completedLessons / totalLessons) * 100;

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Projects Section */}
      <Card className="border-border/30 shadow-md">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <FolderCode className="h-4 w-4 text-primary" />
              הפרויקטים שלי
            </CardTitle>
            <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-primary/20 hover:text-primary">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-1">
            {mockProjects.map(project => (
              <ProjectItem key={project.id} project={project} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Learning Path Section */}
      {gamificationEnabled && (
        <Card className="flex-1 border-border/30 shadow-md overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Trophy className="h-4 w-4 text-gamification-xp" />
              מסלול הלמידה
            </CardTitle>
            <div className="mt-2">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-muted-foreground">התקדמות</span>
                <span className="font-medium">{completedLessons}/{totalLessons}</span>
              </div>
              <Progress value={progress} className="h-1.5" />
            </div>
          </CardHeader>
          <CardContent className="pt-0 pb-0">
            <ScrollArea className="h-[calc(100%-1rem)] pr-2">
              <div className="py-2">
                {learningPath.map((item, index) => (
                  <PathItem key={item.id} item={item} index={index} />
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
